import { MergeEngineClient } from "../engine/client";
import { clearElement, createElement, prettyJson, requireElement } from "../ui/dom";
import {
  STORY_BASE,
  storyBranch,
} from "./fixtures";
import { GuidedStoryMachine } from "./machine";
import { storyTimeline, storyValidatorRows } from "./presentation";
import { GUIDED_STORY_TEMPLATE } from "./template";
import type {
  StoryBranch,
  StoryPhase,
  StorySnapshot,
  StoryTimelineStep,
} from "./types";

const PHASE_COPY: Readonly<
  Record<StoryPhase, { readonly label: string; readonly detail: string }>
> = {
  ready: {
    label: "Ready to begin",
    detail: "No protocol step or merge operation has run.",
  },
  tct_verified: {
    label: "TCT projection verified",
    detail: "Exact M-URL bytes and representation metadata are known.",
  },
  sbr_observed: {
    label: "Complete SBR base retained",
    detail: "The client now holds the target-specific AST Core validator.",
  },
  source_prepared: {
    label: "Local source prepared",
    detail: "The complete local value has no invented server validator.",
  },
  concurrent_committed: {
    label: "Intervening commit shown to viewer",
    detail: "The simulated client remains unaware and still holds the base ETag.",
  },
  precondition_failed: {
    label: "412 rejected the stale PUT",
    detail: "No client mutation occurred and blind retry remains prohibited.",
  },
  target_refreshed: {
    label: "Current target retrieved",
    detail: "The exact base/source/target triple is ready for the pinned worker.",
  },
  merge_pending: {
    label: "Pinned merge worker running",
    detail: "Reset or branch change invalidates this run and any late response.",
  },
  merged_ready: {
    label: "Complete candidate passed policy",
    detail: "Explicit confirmation is still required before one simulated retry.",
  },
  conflict: {
    label: "Conflict at /title",
    detail: "No partial result, retry, confirmation, or client commit is available.",
  },
  committed: {
    label: "One simulated atomic commit",
    detail: "The fresh SBR validator guarded exactly one complete replacement.",
  },
  stopped: {
    label: "Run stopped fail-closed",
    detail: "No confirmation, retry, or client commit is available.",
  },
};

const NEXT_LABEL: Readonly<Partial<Record<StoryPhase, string>>> = {
  ready: "Verify TCT delivery",
  tct_verified: "Read the complete SBR",
  sbr_observed: "Prepare the local source",
  source_prepared: "Show the intervening writer",
  concurrent_committed: "Attempt stale AST Core PUT",
  precondition_failed: "Retrieve the current target",
  target_refreshed: "Run the pinned merge",
};

function setText(selector: string, value: string): void {
  requireElement<HTMLElement>(selector).textContent = value;
}

function countLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function renderTimelineStep(step: StoryTimelineStep): HTMLElement {
  const article = createElement("article", "timeline-step");
  article.dataset.tone = step.tone;
  const number = createElement("span", "timeline-step__number", step.number);
  number.setAttribute("aria-hidden", "true");
  const copy = createElement("div", "timeline-step__copy");
  copy.append(
    createElement("p", "eyebrow", step.eyebrow),
    createElement("h4", undefined, step.title),
    createElement("p", "timeline-step__detail", step.detail),
  );
  const wire = createElement("pre", "timeline-step__wire");
  wire.textContent = step.wire;
  article.append(number, copy, wire);
  return article;
}

function progressState(
  snapshot: StorySnapshot,
  stage: "tct" | "semantic" | "ast" | "merge",
): "idle" | "active" | "complete" | "rejected" {
  const phase = snapshot.phase;
  if (stage === "tct" || stage === "semantic") {
    return phase === "ready" ? "idle" : "complete";
  }
  if (stage === "ast") {
    if (
      phase === "ready" ||
      phase === "tct_verified"
    ) {
      return "idle";
    }
    if (
      phase === "sbr_observed" ||
      phase === "source_prepared" ||
      phase === "concurrent_committed" ||
      phase === "precondition_failed"
    ) {
      return "active";
    }
    return "complete";
  }
  if (
    phase === "ready" ||
    phase === "tct_verified" ||
    phase === "sbr_observed" ||
    phase === "source_prepared" ||
    phase === "concurrent_committed" ||
    phase === "precondition_failed" ||
    phase === "target_refreshed"
  ) {
    return "idle";
  }
  if (phase === "merge_pending" || phase === "merged_ready") {
    return "active";
  }
  if (phase === "conflict" || phase === "stopped") {
    return "rejected";
  }
  return "complete";
}

function progressLabel(state: ReturnType<typeof progressState>): string {
  switch (state) {
    case "idle":
      return "Waiting";
    case "active":
      return "In progress";
    case "complete":
      return "Complete";
    case "rejected":
      return "Stopped";
  }
}

function renderProgress(snapshot: StorySnapshot): void {
  for (const stage of ["tct", "semantic", "ast", "merge"] as const) {
    const state = progressState(snapshot, stage);
    const node = requireElement<HTMLElement>(`#story-progress-${stage}`);
    node.dataset.state = state;
    const label = requireElement<HTMLElement>(
      `#story-progress-${stage} small`,
    );
    label.textContent = progressLabel(state);
  }
}

function renderValidatorRows(snapshot: StorySnapshot): void {
  const body = requireElement<HTMLTableSectionElement>("#story-validator-rows");
  clearElement(body);
  for (const row of storyValidatorRows(snapshot)) {
    const tableRow = document.createElement("tr");
    for (const value of [
      row.state,
      row.sbrEtag,
      row.murlEtag,
      row.semanticEtag,
      row.meaning,
    ]) {
      tableRow.append(createElement("td", undefined, value));
    }
    body.append(tableRow);
  }
}

function renderEngine(snapshot: StorySnapshot): void {
  const container = requireElement<HTMLElement>("#story-engine-result");
  if (snapshot.phase === "merge_pending") {
    container.dataset.state = "running";
    setText("#story-engine-title", "Bounded worker running");
    setText(
      "#story-engine-detail",
      "The response must remain bound to this run, branch, and exact input triple.",
    );
    setText("#story-engine-raw", `Run ${snapshot.runId}: pending`);
    return;
  }
  if (snapshot.mergeResult !== null) {
    const status = snapshot.mergeResult.response.status;
    container.dataset.state = status === "merged" ? "success" : "rejected";
    setText(
      "#story-engine-title",
      status === "merged" ? "Complete candidate" : "Complete conflict set",
    );
    setText(
      "#story-engine-detail",
      status === "merged"
        ? "The fixed application gate passed; confirmation is still separate."
        : "No partial merged object or retry exists.",
    );
    setText("#story-engine-raw", snapshot.mergeResult.raw);
    return;
  }
  if (snapshot.phase === "stopped") {
    container.dataset.state = "rejected";
    setText("#story-engine-title", "Worker result rejected");
    setText(
      "#story-engine-detail",
      snapshot.error ?? "The active run stopped fail-closed.",
    );
    setText("#story-engine-raw", "No accepted merge response.");
    return;
  }
  container.dataset.state = "idle";
  setText("#story-engine-title", "Not invoked");
  setText(
    "#story-engine-detail",
    "The engine runs only after the current SBR target is retrieved.",
  );
  setText("#story-engine-raw", "No merge response.");
}

function outcomeCopy(snapshot: StorySnapshot): {
  mode: string;
  label: string;
  detail: string;
} {
  const fixture = storyBranch(snapshot.branch);
  if (snapshot.phase === "committed") {
    return {
      mode: "AST Core · complete replacement",
      label: "204 · One simulated commit",
      detail:
        "The current SBR ETag matched and the complete candidate became the next state.",
    };
  }
  if (snapshot.phase === "conflict") {
    return {
      mode: "Deterministic merge · intervention boundary",
      label: "Conflict · zero client commits",
      detail:
        "Concurrent title edits produced /title and stopped the story without a partial value.",
    };
  }
  if (snapshot.phase === "merged_ready") {
    return {
      mode: "Application gate · passed",
      label: "Candidate ready · not yet confirmed",
      detail:
        "Merge success is not approval. Explicit confirmation is required to continue this simulation.",
    };
  }
  if (snapshot.phase === "stopped") {
    return {
      mode: "Fail-closed worker boundary",
      label: "Stopped · zero client commits",
      detail: snapshot.error ?? "The active run stopped.",
    };
  }
  if (snapshot.phase === "ready") {
    return {
      mode: "Fixed application contract",
      label: "Ready · zero commits",
      detail: "The client has not observed or changed any state.",
    };
  }
  return {
    mode: fixture.label,
    label:
      snapshot.phase === "precondition_failed"
        ? "412 · Zero client commits"
        : "Story in progress · zero client commits",
    detail: PHASE_COPY[snapshot.phase].detail,
  };
}

export function mountGuidedStory(): void {
  const app = requireElement<HTMLDivElement>("#app");
  app.innerHTML = GUIDED_STORY_TEMPLATE;

  const engine = new MergeEngineClient();
  const machine = new GuidedStoryMachine(engine);
  const branchSelect = requireElement<HTMLSelectElement>("#story-branch-select");
  const nextButton = requireElement<HTMLButtonElement>("#story-next");
  const confirmButton = requireElement<HTMLButtonElement>("#story-confirm");
  const resetButton = requireElement<HTMLButtonElement>("#story-reset");

  function render(): void {
    const snapshot = machine.snapshot;
    const fixture = storyBranch(snapshot.branch);
    const phaseCopy = PHASE_COPY[snapshot.phase];

    branchSelect.value = snapshot.branch;
    setText("#story-branch-summary", fixture.summary);
    setText("#story-run-id", `#${snapshot.runId}`);
    setText("#story-phase-label", phaseCopy.label);
    setText("#story-phase-explanation", phaseCopy.detail);
    setText("#story-base-json", prettyJson(STORY_BASE));
    setText("#story-source-json", prettyJson(fixture.source));
    setText("#story-target-json", prettyJson(fixture.target));

    const nextLabel = NEXT_LABEL[snapshot.phase];
    nextButton.hidden = nextLabel === undefined;
    nextButton.disabled = snapshot.phase === "merge_pending";
    if (nextLabel !== undefined) {
      nextButton.textContent = nextLabel;
    }
    confirmButton.hidden = snapshot.phase !== "merged_ready";
    confirmButton.disabled = snapshot.phase !== "merged_ready";

    renderProgress(snapshot);
    renderValidatorRows(snapshot);
    renderEngine(snapshot);

    const outcome = outcomeCopy(snapshot);
    const outcomeNode = requireElement<HTMLElement>("#story-outcome");
    outcomeNode.dataset.state = snapshot.phase;
    setText("#story-outcome-mode", outcome.mode);
    setText("#story-outcome-label", outcome.label);
    setText("#story-outcome-detail", outcome.detail);
    setText(
      "#story-outcome-counts",
      `${countLabel(snapshot.clientCommitCount, "client commit")} · ${countLabel(snapshot.externalCommitCount, "intervening commit")}`,
    );

    const timeline = requireElement<HTMLElement>("#story-timeline");
    clearElement(timeline);
    for (const step of storyTimeline(snapshot)) {
      timeline.append(renderTimelineStep(step));
    }
  }

  branchSelect.addEventListener("change", () => {
    const branch = branchSelect.value as StoryBranch;
    if (branch === "merge" || branch === "conflict") {
      machine.selectBranch(branch);
      render();
    }
  });

  nextButton.addEventListener("click", () => {
    if (machine.snapshot.phase === "target_refreshed") {
      const pending = machine.runMerge();
      render();
      void pending.then(() => render());
      return;
    }
    machine.advance();
    render();
  });

  confirmButton.addEventListener("click", () => {
    machine.confirmAndCommit();
    render();
  });

  resetButton.addEventListener("click", () => {
    machine.reset();
    render();
  });

  window.addEventListener("beforeunload", () => {
    engine.dispose();
  });

  render();
}
